import { logger } from '@/core/logging/logger'

/**
 * Ask the browser not to evict our IndexedDB data.
 *
 * An installed home-screen PWA is already exempt from Safari's 7-day eviction,
 * so on the target platform this is belt-and-braces. It matters on desktop
 * browsers and it costs nothing.
 *
 * iOS may not grant it permanently, so this is called on every launch rather
 * than once at install. 04-architecture.md §6.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) {
    logger.debug('app', 'Storage persistence API unavailable')
    return false
  }

  try {
    if (await navigator.storage.persisted()) {
      logger.debug('app', 'Storage already persistent')
      return true
    }

    const granted = await navigator.storage.persist()
    logger.info('app', granted ? 'Storage persistence granted' : 'Storage persistence denied')
    return granted
  } catch (err) {
    logger.warn('app', 'Storage persistence request failed', err)
    return false
  }
}

export interface StorageUsage {
  usageBytes: number
  quotaBytes: number
  persistent: boolean
}

/** Powers the storage display in Settings (S12). */
export async function getStorageUsage(): Promise<StorageUsage | null> {
  if (!navigator.storage?.estimate) return null

  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate()
    const persistent = (await navigator.storage.persisted?.()) ?? false
    return { usageBytes: usage, quotaBytes: quota, persistent }
  } catch (err) {
    logger.warn('app', 'Storage estimate failed', err)
    return null
  }
}
